import {QueueListIcon} from "@heroicons/react/16/solid";
import {PROCESS_COPY, ProcessTraySummary} from "./process-ui";

export type ProcessTrayTriggerProps = {
  summary: ProcessTraySummary;
  /** Hide the text label, e.g. on a narrow navbar; the accessible name stays. */
  iconOnly?: boolean;
};

/** The accessible name, which carries the state the dot or spinner shows. */
export const processTrayAccessibleName = (summary: ProcessTraySummary): string => {
  const parts: string[] = [PROCESS_COPY.trayLabel];
  if (summary.activeCount > 0) parts.push(`${summary.activeCount} running`);
  if (summary.unseenProblem) parts.push("needs attention");
  else if (summary.unseenCount > 0) parts.push(summary.unseenCount === 1 ? "1 new result" : `${summary.unseenCount} new results`);
  return parts.join(", ");
};

// sized here rather than by NavbarItem, which only sizes icons that are its direct children
const ICON_CLASS = "size-5 shrink-0";

const Spinner = () => <svg
  viewBox="0 0 16 16"
  fill="none"
  aria-hidden="true"
  className={[ICON_CLASS, "animate-spin text-[var(--ots-primary)] motion-reduce:animate-none"].join(" ")}>
  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity=".3"/>
  <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
</svg>;

/**
 * What the navbar shows for processes: quiet when there's nothing to say, a spinner and count while work runs, and a
 * dot for unseen results (red when one failed, which wins over green).
 */
export const ProcessTrayTrigger = ({summary, iconOnly = false}: ProcessTrayTriggerProps) => {
  const dotColor = summary.unseenProblem ? "bg-[var(--ots-error)]" : "bg-[var(--ots-success)]";
  const showDot = summary.unseenProblem || (summary.state === "complete");

  return <span data-state={summary.state} className="relative inline-flex items-center gap-2">
    <span className="relative inline-flex">
      {summary.state === "processing"
        ? <Spinner/>
        : <QueueListIcon aria-hidden="true" className={[ICON_CLASS, "fill-zinc-500 dark:fill-zinc-400"].join(" ")}/>}
      {showDot && <span
        data-testid="process-tray-dot"
        aria-hidden="true"
        className={["absolute -right-1 -top-1 size-2 rounded-full ring-2 ring-white dark:ring-[#081222]", dotColor].join(" ")}/>}
    </span>
    {!iconOnly && <span className="truncate">
      {summary.state === "processing" ? `${summary.activeCount} running` : PROCESS_COPY.trayLabel}
    </span>}
  </span>;
};
